import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useAdmin } from './AdminContext';

export default function AlertsTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [usageAlerts, setUsageAlerts] = useState([]);
  const [alertsPeriod, setAlertsPeriod] = useState('today');

  const loadUsageAlerts = useCallback(async (period = 'today') => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/usage-alerts?period=${period}`);
      if (response.ok) {
        const data = await response.json();
        setUsageAlerts(data.users || []);
      }
    } catch (error) {
      console.error('Error loading usage alerts:', error);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadUsageAlerts(alertsPeriod); }, [refreshTrigger]);

  return (
    <div className="space-y-6" data-testid="usage-alerts-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Usage Alerts</h2>
        <div className="flex gap-2">
          <select data-testid="alerts-period-select" value={alertsPeriod}
            onChange={(e) => { setAlertsPeriod(e.target.value); loadUsageAlerts(e.target.value); }}
            className="bg-slate-700 text-white border border-slate-600 rounded px-3 py-1.5 text-sm">
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
          </select>
          <Button onClick={() => loadUsageAlerts(alertsPeriod)} variant="outline" size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-700" data-testid="alerts-refresh-btn">
            Refresh
          </Button>
        </div>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="usage-alerts-table">
              <thead>
                <tr className="border-b border-slate-700 text-left">
                  <th className="p-3 text-slate-400 font-medium">User</th>
                  <th className="p-3 text-slate-400 font-medium">Email</th>
                  <th className="p-3 text-slate-400 font-medium">Total Requests</th>
                  <th className="p-3 text-slate-400 font-medium">Breakdown</th>
                  <th className="p-3 text-slate-400 font-medium">Status</th>
                  <th className="p-3 text-slate-400 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {usageAlerts.length === 0 ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">No AI usage recorded for this period</td></tr>
                ) : (
                  usageAlerts.map((u, i) => (
                    <tr key={u.user_id} className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${u.total_requests >= 15 ? 'bg-red-500/5' : ''}`} data-testid={`alert-row-${i}`}>
                      <td className="p-3 text-white">{u.name}</td>
                      <td className="p-3 text-slate-300 text-xs">{u.email}</td>
                      <td className="p-3">
                        <span className={`font-mono font-bold ${u.total_requests >= 15 ? 'text-red-400' : u.total_requests >= 8 ? 'text-amber-400' : 'text-green-400'}`}>
                          {u.total_requests}
                        </span>
                      </td>
                      <td className="p-3 text-slate-400 text-xs">
                        {(u.features || []).map(f => `${f.feature}: ${f.count}`).join(', ') || '-'}
                      </td>
                      <td className="p-3">
                        {u.is_blocked ? (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400">Blocked</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">Active</span>
                        )}
                      </td>
                      <td className="p-3">
                        <Button data-testid={`block-btn-${i}`}
                          onClick={async () => {
                            const action = u.is_blocked ? 'unblock' : 'block';
                            try {
                              const resp = await adminFetch(`${API_URL}/api/v2/admin/${action}-user/${u.user_id}`, { method: 'POST' });
                              if (resp.ok) { toast.success(`User ${action}ed`); loadUsageAlerts(alertsPeriod); }
                              else { toast.error(`Failed to ${action} user`); }
                            } catch (e) { toast.error('Error'); }
                          }}
                          variant="outline" size="sm"
                          className={u.is_blocked ? 'border-green-600 text-green-400 hover:bg-green-700/30' : 'border-red-600 text-red-400 hover:bg-red-700/30'}>
                          {u.is_blocked ? 'Unblock' : 'Block'}
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <h3 className="text-sm font-medium text-slate-400 mb-2">Daily Limits</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">AI Chat:</span> <span className="text-white font-mono">20/day</span></div>
            <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">Fridge Scan:</span> <span className="text-white font-mono">10/day</span></div>
            <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">Voice Food:</span> <span className="text-white font-mono">15/day</span></div>
            <div className="bg-slate-700/50 rounded p-2"><span className="text-slate-400">Photo Scan:</span> <span className="text-white font-mono">10/day</span></div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
